import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { QuickLoggerFactory } from '@/lib/universal-logger';

// Initialize auth namespace logger for session management operations
const authLogger = QuickLoggerFactory.auth();

/**
 * SessionManager provides additional security controls on top of Supabase's 
 * built-in session management.
 * 
 * NOTE: This implementation uses Supabase's built-in session management for most operations,
 * as it already provides secure JWT tokens, HttpOnly cookies, and automatic token refresh.
 */
export class SessionManager {
  // Default session timeout (15 minutes)
  private static readonly SESSION_TIMEOUT = 15 * 60 * 1000;
  
  /**
   * Gets the current active session
   * @returns The current session or null if not authenticated
   */
  static async getCurrentSession(): Promise<Session | null> {
    const { data } = await supabase.auth.getSession();
    
    authLogger.debug("Retrieved session", {
      hasSession: !!data.session,
      operation: 'getCurrentSession'
    });
    
    return data.session;
  }

  /**
   * Validates if the current session is valid and not expired
   * @returns True if the session is valid, false otherwise
   */
  static async validateCurrentSession(): Promise<boolean> {
    const session = await this.getCurrentSession();
    
    if (!session) {
      return false;
    }
    
    // Check if the session has expired
    // Supabase handles token refresh automatically, but this provides an additional check
    const expiresAt = new Date(session.expires_at || 0).getTime();
    const now = new Date().getTime();
    
    if (expiresAt < now) {
      authLogger.warn("Session has expired and wasn't auto-refreshed", {
        expiresAt: new Date(expiresAt).toISOString(),
        now: new Date(now).toISOString(),
        sessionId: session.access_token?.substring(0, 10) + "...",
        operation: 'validateCurrentSession'
      });
      return false;
    }
    
    return true;
  }

  /**
   * Refreshes the current session
   * @returns The refreshed session or null if refresh failed
   */
  static async refreshSession(): Promise<Session | null> {
    const { data, error } = await supabase.auth.refreshSession();
    
    if (error) {
      authLogger.error("Error refreshing session", {
        error: {
          message: error.message,
          status: error.status,
          code: error.name
        },
        operation: 'refreshSession'
      });
      return null;
    }
    
    return data.session;
  }

  /**
   * Ends the current session and performs cleanup
   */
  static async endSession(): Promise<void> {
    authLogger.info("Ending session", {
      operation: 'endSession',
      scope: 'local'
    });
    
    await supabase.auth.signOut({ scope: 'local' });
    
    authLogger.info("Session ended successfully", {
      operation: 'endSession'
    });
  }

  /**
   * Gets the current authenticated user
   * @returns The current user or null if not authenticated
   */
  static async getCurrentUser(): Promise<User | null> {
    const { data, error } = await supabase.auth.getUser();
    
    if (error) {
      // Only log as error if it's not a missing session (expected for anonymous users)
      if (error.message?.includes('Auth session missing') || error.name === 'AuthSessionMissingError') {
        authLogger.debug("No active session (anonymous user)", {
          operation: 'getCurrentUser'
        });
      } else {
        authLogger.error("Error getting user", {
          error: {
            message: error.message,
            status: error.status,
            code: error.name
          },
          operation: 'getCurrentUser'
        });
      }
      return null;
    }
    
    return data.user;
  }

  /**
   * Checks if a session meets security requirements like recent authentication
   * for sensitive operations.
   * 
   * @param session The session to check
   * @param requiredAuthTimeCutoff Maximum session age in milliseconds for sensitive operations
   * @returns True if the session is secure enough for the operation, false otherwise
   */
  static isSessionSecureForOperation(
    session: Session | null,
    requiredAuthTimeCutoff: number = this.SESSION_TIMEOUT
  ): boolean {
    if (!session) {
      return false;
    }
    
    // For highly sensitive operations, we might want to ensure recent authentication
    // Supabase sessions don't expose created_at directly, but we can use expires_at
    // and the standard session duration to estimate the creation time
    const expiresAt = new Date(session.expires_at || 0).getTime();
    const estimatedCreationTime = expiresAt - (24 * 60 * 60 * 1000); // Assuming standard 24h session
    const now = new Date().getTime();
    
    return (now - estimatedCreationTime) < requiredAuthTimeCutoff;
  }
}